/**
 * AppShell - Top-level layout component for the Kira Productivity Suite.
 *
 * Provides the persistent navigation bar (left sidebar), shared header,
 * and module router that renders the active module via React.lazy + Suspense.
 * Module switching is state-driven via useUIStore.activeModule (no URL routing).
 *
 * Also hosts:
 * - Animated gradient orbs background
 * - Pomodoro timer tick (runs across all modules)
 * - Pomodoro notifications (audio, desktop, visual banner, ARIA live region)
 *
 * Layout: flex row with NavigationBar on the left, main content (SharedHeader + ModuleRouter) on the right.
 *
 * @see Requirements 1.1, 1.2, 1.6, 7.8, 7.9, 13.1, 13.2
 */

import { useEffect } from 'react';
import { useUIStore } from '@/stores/uiStore';
import { usePomodoroTick } from '@/hooks/usePomodoroTick';
import { useCalendarSync } from '@/hooks/useCalendarSync';
import { useProductivitySync } from '@/hooks/useProductivitySync';
import { useInitialHydrate } from '@/hooks/useInitialHydrate';
import { useJournalShortcut } from '@/hooks/useJournalShortcut';
import { NavigationBar } from './NavigationBar';
import { SharedHeader } from './SharedHeader';
import { ModuleRouter } from './ModuleRouter';
import { PomodoroNotifications } from '@/components/pomodoro/PomodoroNotifications';
import { OAuthCallbackHandler } from '@/components/calendar/OAuthCallbackHandler';
import { Toaster } from '@/components/ui/Toaster';

// ─── Animated Background ─────────────────────────────────────────────────────

function AnimatedBackground() {
  return (
    <div className="fixed inset-0 -z-10 overflow-hidden" aria-hidden="true">
      <div className="absolute top-[-20%] left-[-10%] w-[500px] h-[500px] rounded-full bg-violet-600/20 blur-[120px] animate-float-slow" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[400px] h-[400px] rounded-full bg-fuchsia-500/15 blur-[100px] animate-float-slower" />
      <div className="absolute top-[40%] right-[20%] w-[300px] h-[300px] rounded-full bg-cyan-500/[0.12] blur-[80px] animate-float-medium" />
      <div className="absolute top-[10%] right-[50%] w-[250px] h-[250px] rounded-full bg-amber-400/[0.1] blur-[60px] animate-float-slower" />
      <div className="absolute bottom-[20%] left-[30%] w-[350px] h-[350px] rounded-full bg-pink-500/[0.12] blur-[90px] animate-float-slow" />
    </div>
  );
}

// ─── AppShell Component ──────────────────────────────────────────────────────

export function AppShell() {
  // Restore last active module on mount (once)
  useEffect(() => {
    useUIStore.getState().restoreLastActiveModule();
  }, []);

  // Drive the Pomodoro timer at the app shell level (runs across all modules)
  usePomodoroTick();

  // Start calendar sync polling when online (runs across all modules)
  useCalendarSync();

  // Drain the productivity-module offline sync queue (tasks/habits/notes)
  // when online (runs across all modules). Kicked off before the hydrate so a
  // server pull never races ahead of pushing pending local changes.
  useProductivitySync();

  // One-time pull-from-server hydrate on login so a fresh browser gets the
  // user's data (journal + tasks/habits/notes), version-guarded to never
  // clobber newer/pending local changes.
  useInitialHydrate();

  // Register the hidden-journal reveal shortcut (Ctrl/Cmd+Shift+J).
  useJournalShortcut();

  return (
    <>
      {/* Animated gradient orbs behind everything */}
      <AnimatedBackground />

      <div className="flex h-screen overflow-hidden relative">
        {/* Left navigation sidebar — glassmorphism style */}
        <NavigationBar />

        {/* Main content area: header + active module */}
        <div className="flex-1 flex flex-col min-w-0">
          <SharedHeader />
          <ModuleRouter />
        </div>

        {/* Pomodoro notifications (banner, screen flash, ARIA live region) — always mounted */}
        <PomodoroNotifications />

        {/* Global toast notifications (sync/error feedback) — always mounted */}
        <Toaster />

        {/* OAuth callback handler — detects OAuth redirect params on app load */}
        <OAuthCallbackHandler />
      </div>
    </>
  );
}
